import "./StripeConnectOnboardingModals.css";

import {
  Box,
  Button,
  Dialog,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";

import { initStripeConnectOnboarding } from "Config/API/action";
import {
  getGeneralInfo,
  getStripeConnectCountries,
  getUserDetails,
} from "Config/API/get";
import {
  getStripeConnectedIdFromAdminProfile,
  isEmptyStripeConnectedId,
  isSuperadminOrgAdmin,
} from "Helper/stripeConnectedAccount";

const AUTH_ROUTE_PREFIXES = [
  "/login",
  "/verification",
  "/error",
  "/account/return",
  "/account/refresh",
];

export default function StripeConnectOnboardingModals() {
  const location = useLocation();
  const loggedInUserId = useSelector((s) => s?.authenticationReducer?.id);
  const role = useSelector((s) => s?.authenticationReducer?.role);
  const orgId = useSelector((s) => s?.authenticationReducer?.org_id);
  const userStripeConnectedId = useSelector(
    (s) => s?.authenticationReducer?.stripe_connected_id
  );

  const [flowStep, setFlowStep] = useState(null);
  const [eligibilityResolved, setEligibilityResolved] = useState(false);
  const [country, setCountry] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [countryOptions, setCountryOptions] = useState([]);
  const [countriesLoading, setCountriesLoading] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token || !loggedInUserId) {
      setFlowStep(null);
      setEligibilityResolved(true);
      return;
    }
    if (role === "super_admin" || role === "employee") {
      setFlowStep(null);
      setEligibilityResolved(true);
      return;
    }
    if (AUTH_ROUTE_PREFIXES.some((p) => location.pathname.startsWith(p))) {
      setFlowStep(null);
      setEligibilityResolved(true);
      return;
    }

    const loggedInUser = {
      role,
      org_id: orgId,
      stripe_connected_id: userStripeConnectedId,
    };

    let cancelled = false;
    setFlowStep(null);
    setEligibilityResolved(false);

    const resolveEligibility = async () => {
      try {
        if (isSuperadminOrgAdmin(loggedInUser)) {
          const userRes = await getUserDetails(token);
          if (cancelled) return;
          if (!userRes?.data?.status) {
            setFlowStep(null);
            return;
          }
          const stripeId = userRes?.data?.data?.stripe_connected_id;
          setFlowStep(
            isEmptyStripeConnectedId(stripeId) ? "country" : null
          );
          return;
        }

        const res = await getGeneralInfo(token);
        if (cancelled || !res?.status) {
          if (!cancelled) setFlowStep(null);
          return;
        }
        const orgStripeId = res?.org_info?.stripe_connected_id;
        const rootStripeId = res?.stripe_connected_id;
        const stripeId = getStripeConnectedIdFromAdminProfile(res);
        const shouldShowPopup =
          (isEmptyStripeConnectedId(orgStripeId) &&
            isEmptyStripeConnectedId(rootStripeId)) ||
          isEmptyStripeConnectedId(stripeId);

        setFlowStep(shouldShowPopup ? "country" : null);
      } catch {
        if (!cancelled) setFlowStep(null);
      } finally {
        if (!cancelled) setEligibilityResolved(true);
      }
    };

    resolveEligibility();

    return () => {
      cancelled = true;
    };
  }, [loggedInUserId, role, orgId, userStripeConnectedId, location.pathname]);

  const token = localStorage.getItem("token");
  const selectedCountryCode = country;

  const normalizeCountryOptions = (res) => {
    const rawList =
      res?.countries ??
      res?.data?.countries ??
      res?.result?.countries ??
      res?.result ??
      res?.data ??
      [];

    if (!Array.isArray(rawList)) return [];

    return rawList
      .map((item) => {
        if (typeof item === "string") {
          const code = item.trim().toUpperCase();
          return code ? { code, name: code } : null;
        }
        const code = (
          item?.code ??
          item?.country_code ??
          item?.countryCode ??
          item?.iso2 ??
          item?.value ??
          ""
        )
          .toString()
          .trim()
          .toUpperCase();
        const name = (
          item?.name ??
          item?.country ??
          item?.label ??
          code
        )
          .toString()
          .trim();
        if (!code) return null;
        return { code, name: name || code };
      })
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  useEffect(() => {
    if (flowStep !== "country" || !token) return;

    let cancelled = false;
    setCountriesLoading(true);
    getStripeConnectCountries(token)
      .then((res) => {
        if (cancelled) return;
        setCountryOptions(normalizeCountryOptions(res));
      })
      .catch(() => {
        if (!cancelled) {
          setCountryOptions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setCountriesLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [flowStep, token]);

  async function redirectToStripe(selectedCountryCodeParam, userToken) {
    if (!selectedCountryCodeParam || !userToken || submitting) return;
    setSubmitting(true);
    try {
      const appOrigin = window.location.origin;
      const refreshUrl = `${appOrigin}/refresh`;
      const returnUrl = `${appOrigin}/return`;
      const data = await initStripeConnectOnboarding(
        userToken,
        selectedCountryCodeParam,
        refreshUrl,
        returnUrl
      );

      if (!data?.status || !data?.data?.onboarding_url) {
        throw new Error(data?.message || "Unable to start Stripe onboarding");
      }

      const accountId = data?.data?.account_id;
      if (accountId) {
        sessionStorage.setItem("stripe_account_id", accountId);
        localStorage.setItem("stripe_account_id", accountId);
      }
      sessionStorage.setItem("stripe_country_code", selectedCountryCodeParam);
      localStorage.setItem("stripe_country_code", selectedCountryCodeParam);
      // window.location.assign(data.data.onboarding_url);
       window.open(data.data.onboarding_url, "_blank");
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Stripe onboarding failed";
      alert(message);
    } finally {
      setSubmitting(false);
    }
  }

  const onNext = () => {
    if (!selectedCountryCode) return alert("Please select a country");
    redirectToStripe(selectedCountryCode, token);
  };

  const countryOpen = eligibilityResolved && flowStep === "country";

  return (
    <>
      <Dialog
        open={countryOpen}
        //  onClose={() => setFlowStep(null)}
        onClose={(_, reason) => {
          if (reason === "backdropClick" || reason === "escapeKeyDown") return;
        }}
        maxWidth={false}
        className="stripe-connect-dialog"
        slotProps={{
          paper: { className: "stripe-connect-paper" }
        }}
      >
        <Box className="stripe-connect-body">
           <button
            type="button"
            className="stripe-connect-close-btn"
            onClick={() => setFlowStep(null)}
            aria-label="Close Stripe onboarding popup"
          >
            X
          </button> 
          <h2 className="stripe-connect-title">Connect your Stripe account</h2>
          <p className="stripe-connect-copy">
            Your organization needs a Stripe account to receive payouts and
            manage money features in Amnius Connect. You will choose your
            country and complete a short Stripe setup on the next screens.
          </p>
          <FormControl fullWidth margin="normal" variant="outlined">
            <InputLabel id="stripe-country-label">Select Country</InputLabel>
            <Select
              labelId="stripe-country-label"
              value={country}
              label="Select Country"
              onChange={(e) => setCountry(e.target.value)}
            >
              <MenuItem value="">
                <em>-Select-</em>
              </MenuItem>
              {countryOptions.map(({ code, name }) => (
                <MenuItem key={code} value={code}>
                  {name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            fullWidth
            disabled={!country || submitting || countriesLoading}
            className="stripe-connect-primary-btn"
            onClick={onNext}
          >
            {submitting
              ? "Please wait..."
              : countriesLoading
                ? "Loading countries..."
                : "Next"}
          </Button>
        </Box>
      </Dialog>
    </>
  );
}
